import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { apiClient } from "@/api/client";
import { captureEntityMutation } from "@/analytics/posthog";
import {
  type LanguageCode,
  type ThemeMode,
  type AppMode,
  type UpdateUserPreferencesPayload,
  normalizeAppMode,
  normalizeLanguage,
  serializeLanguage,
  normalizeTheme,
  serializeTheme,
} from "@/preferences/types";

const PREFERENCES_STORAGE_KEY = "hey-jerry-preferences";

const getStoredLanguage = (): LanguageCode => {
  if (typeof window === "undefined") return "en";

  try {
    return normalizeLanguage(window.localStorage.getItem("hey-jerry-language"));
  } catch {
    return "en";
  }
};

const getStoredTheme = (): ThemeMode => {
  if (typeof window === "undefined") return "system";

  try {
    return normalizeTheme(window.localStorage.getItem("hey-jerry-theme"));
  } catch {
    return "system";
  }
};

const persistLanguage = (language: LanguageCode) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem("hey-jerry-language", language);
  } catch {
    // Silent error handling
  }
};

const persistTheme = (theme: ThemeMode) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem("hey-jerry-theme", theme);
  } catch {
    // Silent error handling
  }
};

type PreferencesState = {
  language: LanguageCode;
  theme: ThemeMode;
  appMode: AppMode;
  appModeHydrated: boolean;
  serverLanguage: LanguageCode;
  serverTheme: ThemeMode;
  serverAppMode: AppMode;
  preferencesHydrated: boolean;
  hydrateFromServer: (profile: { language?: unknown; theme?: unknown; advanced_mode?: unknown } | null) => void;
  setLanguageLocal: (language: LanguageCode) => void;
  setThemeLocal: (theme: ThemeMode) => void;
  saveAppMode: (appMode: AppMode) => Promise<boolean>;
  saveLanguage: (language: LanguageCode) => Promise<boolean>;
  saveTheme: (theme: ThemeMode) => Promise<boolean>;
  resetToDefaults: () => void;
};

const initialLanguage = getStoredLanguage();
const initialTheme = getStoredTheme();
const initialAppMode: AppMode = "simple";

const persistUserPreference = (payload: { language?: LanguageCode; theme?: ThemeMode; appMode?: AppMode }) => {
  const nextPayload: UpdateUserPreferencesPayload = {};

  if (payload.language) {
    nextPayload.language = serializeLanguage(payload.language);
  }

  if (payload.theme) {
    nextPayload.theme = serializeTheme(payload.theme);
  }

  if (payload.appMode) {
    nextPayload.advanced_mode = payload.appMode === "advanced";
  }

  return apiClient.patch("/users/", nextPayload);
};

export const usePreferencesStore = create<PreferencesState>()(
  devtools(
    persist(
      (set, get) => ({
        language: initialLanguage,
        theme: initialTheme,
        appMode: initialAppMode,
        appModeHydrated: false,
        serverLanguage: initialLanguage,
        serverTheme: initialTheme,
        serverAppMode: initialAppMode,
        preferencesHydrated: false,
        hydrateFromServer: (profile) => {
          const language = normalizeLanguage(profile?.language);
          const theme = normalizeTheme(profile?.theme);
          const appMode = normalizeAppMode(profile?.advanced_mode);

          persistLanguage(language);
          persistTheme(theme);

          set({
            language,
            theme,
            appMode,
            serverLanguage: language,
            serverTheme: theme,
            serverAppMode: appMode,
            appModeHydrated: true,
            preferencesHydrated: true,
          });
        },
        setLanguageLocal: (language) => {
          persistLanguage(language);
          set({ language });
        },
        setThemeLocal: (theme) => {
          persistTheme(theme);
          set({ theme });
        },
        saveLanguage: async (language) => {
          const previousLanguage = get().language;
          const fallbackLanguage = get().serverLanguage;

          persistLanguage(language);
          set({ language });

          try {
            await persistUserPreference({ language });
            captureEntityMutation({
              entity: "profile",
              operation: "update",
              result: "success",
              changedFields: ["language"],
            });
            set({ serverLanguage: language });
            return true;
          } catch (error) {
            captureEntityMutation({
              entity: "profile",
              operation: "update",
              result: "failure",
              error,
              changedFields: ["language"],
            });
            persistLanguage(fallbackLanguage);
            set({ language: fallbackLanguage || previousLanguage });
            return false;
          }
        },
        saveTheme: async (theme) => {
          const previousTheme = get().theme;
          const fallbackTheme = get().serverTheme;

          persistTheme(theme);
          set({ theme });

          try {
            await persistUserPreference({ theme });
            captureEntityMutation({
              entity: "profile",
              operation: "update",
              result: "success",
              changedFields: ["theme"],
            });
            set({ serverTheme: theme });
            return true;
          } catch (error) {
            captureEntityMutation({
              entity: "profile",
              operation: "update",
              result: "failure",
              error,
              changedFields: ["theme"],
            });
            persistTheme(fallbackTheme);
            set({ theme: fallbackTheme || previousTheme });
            return false;
          }
        },
        saveAppMode: async (appMode) => {
          const previousAppMode = get().appMode;
          const fallbackAppMode = get().serverAppMode;

          set({ appMode });

          try {
            const response = await persistUserPreference({ appMode });
            const confirmedAppMode = normalizeAppMode(response.data?.advanced_mode);

            captureEntityMutation({
              entity: "profile",
              operation: "update",
              result: "success",
              changedFields: [],
            });
            set({ appMode: confirmedAppMode, serverAppMode: confirmedAppMode });
            return true;
          } catch (error) {
            captureEntityMutation({
              entity: "profile",
              operation: "update",
              result: "failure",
              error,
              changedFields: [],
            });
            set({ appMode: fallbackAppMode || previousAppMode });
            return false;
          }
        },
        resetToDefaults: () => {
          persistLanguage("en");
          persistTheme("system");
          set({
            language: "en",
            theme: "system",
            appMode: "simple",
            appModeHydrated: false,
            serverLanguage: "en",
            serverTheme: "system",
            serverAppMode: "simple",
            preferencesHydrated: true,
          });
        },
      }),
      {
        name: PREFERENCES_STORAGE_KEY,
        partialize: ({ language, theme, serverLanguage, serverTheme }) => ({
          language,
          theme,
          serverLanguage,
          serverTheme,
        }),
        merge: (persistedState, currentState) => {
          const persistedPreferences = persistedState as Partial<PreferencesState>;

          return {
            ...currentState,
            ...persistedPreferences,
            appMode: currentState.appMode,
            serverAppMode: currentState.serverAppMode,
            appModeHydrated: false,
          };
        },
        onRehydrateStorage: () => () => {
          try {
            const state = usePreferencesStore.getState();
            persistLanguage(state.language);
            persistTheme(state.theme);
            usePreferencesStore.setState({ preferencesHydrated: true });
          } catch {
            // Silent error handling
          }
        },
      },
    ),
    { name: "usePreferencesStore" },
  ),
);

export const getInitialLanguage = () => initialLanguage;
